/** @file Vertex drag: values a world pointer delta gives the selection (Ctrl snap, Shift constrain). */
import { constrainDelta } from '@pose2d/editor-geometry/constrainDelta';
import { dragModifiers } from '@pose2d/editor-geometry/dragModifiers';
import { snapPoint } from '@pose2d/editor-geometry/snapPoint';
import type { DragModifierKeys, GridView } from '@pose2d/editor-geometry/types';
import type { Vec2 } from '@pose2d/math/types';
import { vertexFrames } from './vertexFrames';

/** Everything a drag keeps from pointer-down (the scene may re-pose under the preview; the drag never re-reads it). */
export interface VertexDragStart {
  /** Vertices that move, ascending. */
  indices: readonly number[];
  /** The grabbed vertex: snapping puts its world position on the grid. */
  anchor: number;
  /** World positions at pointer-down, 2 per vertex. */
  world: ArrayLike<number>;
  /** vertexFrames.of at pointer-down. */
  frames: Float64Array;
  /** Start values, 2 per vertex: rest positions (Setup) or the deform offsets at the playhead (Animate). */
  base: readonly number[];
}

/** One pointer move: world delta since pointer-down, held keys, the snap setting and the grid. */
export interface VertexDragMove {
  dx: number;
  dy: number;
  keys: DragModifierKeys;
  snap: boolean;
  grid: GridView;
}

/** The world delta after modifiers: Shift → dominant axis (iso edges too), snap → the anchor lands on a grid point. */
function worldDelta(start: VertexDragStart, move: VertexDragMove): Vec2 {
  const mods = dragModifiers(move.snap, move.keys);
  const d: Vec2 = [move.dx, move.dy];
  if (mods.snap && move.grid.mode !== 'off') {
    const ax = start.world[2 * start.anchor] ?? 0;
    const ay = start.world[2 * start.anchor + 1] ?? 0;
    snapPoint(move.grid, ax + d[0], ay + d[1], d);
    d[0] -= ax;
    d[1] -= ay;
  }
  return mods.constrain ? constrainDelta(move.grid, d[0], d[1], d) : d;
}

/**
 * `values`: a copy of `base` with every dragged vertex moved by J⁻¹ · (world delta), so the drawn vertices move by
 * exactly (dx, dy), the world delta after snap and constrain (the wire preview draws them there).
 */
export function dragVertices(start: VertexDragStart, move: VertexDragMove): { values: number[]; dx: number; dy: number } {
  const [dx, dy] = worldDelta(start, move);
  const values = [...start.base];
  const r: Vec2 = [0, 0];
  for (const v of start.indices) {
    vertexFrames.apply(start.frames, v, dx, dy, r);
    values[2 * v] = (values[2 * v] ?? 0) + r[0];
    values[2 * v + 1] = (values[2 * v + 1] ?? 0) + r[1];
  }
  return { values, dx, dy };
}
